/**
 * Launch offer + global-launch "crumbs".
 *
 * Presentation only — one source of truth for the copy so the same promise
 * follows the learner from the hero to checkout. Change it here, it changes
 * everywhere.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMembership } from "@/hooks/useMembership";
import {
  DUAL_LIST_PRICE_LABEL,
  DUAL_OFFER_PRICE_LABEL,

  OFFER_DISCOUNT_LABEL,
  OFFER_ENDS_LABEL,
  OFFER_IS_FLASH,
  OFFER_NAME,
  OFFER_COUPON_CODE,
} from "@/config/pricing";

export const OFFER = {
  name: OFFER_NAME,
  was: DUAL_LIST_PRICE_LABEL,
  now: DUAL_OFFER_PRICE_LABEL,
  save: OFFER_DISCOUNT_LABEL,
  savingsLabel: OFFER_DISCOUNT_LABEL,
  endsLabel: `Ends ${OFFER_ENDS_LABEL}`,
  code: OFFER_COUPON_CODE,
  codeLabel: `Code ${OFFER_COUPON_CODE}`,
  isFlash: OFFER_IS_FLASH,
  unit: "/ learner / year",
  globalLabel: "Live in",
  globalHighlight: "US and India",
} as const;


/**
 * The offer only exists before someone has paid. Once a membership is active
 * the pills disappear everywhere — no upsell shown to a paying learner.
 */
function useOfferVisible() {
  const { loading, isActive } = useMembership();
  return OFFER_IS_FLASH && !loading && !isActive;
}

/** Full-size hero pill pair. */
export function OfferCrumbs({ className }: { className?: string }) {
  const visible = useOfferVisible();
  if (!visible) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-2.5", className)}>
      <OfferPill />
      <GlobalPill />
    </div>
  );
}

export function OfferPill({ className }: { className?: string }) {
  const visible = useOfferVisible();
  if (!visible) return null;

  return (
    <Link
      to="/payment"
      aria-label={`${OFFER.name} — ${OFFER.now} with code ${OFFER.code}, MRP ${OFFER.was}`}
      className={cn(
        "group inline-flex min-h-[44px] items-center gap-2.5 rounded-full border border-primary/40",
        "bg-primary/10 px-4 text-sm font-semibold text-primary backdrop-blur-sm",
        "transition-colors hover:bg-primary/20",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      <Star className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{OFFER.name}</span>
      <span className="h-4 w-px bg-primary/30" aria-hidden="true" />
      <span className="text-muted-foreground line-through">{OFFER.was}</span>
      <span className="text-foreground">{OFFER.now}</span>
      {OFFER.isFlash && (
        <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-primary-foreground">
          {OFFER.save}
        </span>
      )}
      <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-wider text-primary/80">
        {OFFER.codeLabel}
      </span>

    </Link>
  );
}

export function GlobalPill({ className }: { className?: string }) {
  return (
    <a
      href="#global-waitlist"
      className={cn(
        "group inline-flex min-h-[44px] flex-wrap justify-center gap-x-2 gap-y-1 rounded-3xl border border-accent/40",
        "bg-accent/10 px-4 py-2 text-sm font-semibold text-foreground backdrop-blur-sm",
        "transition-colors hover:bg-accent/20",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      <span>
        {OFFER.globalLabel} <span className="text-accent">{OFFER.globalHighlight}</span>
      </span>
      <span className="text-muted-foreground" aria-hidden="true">·</span>
      <span className="underline underline-offset-4">Coming soon to your country</span>
      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
    </a>
  );
}

/**
 * The crumb: a small, quiet reminder for anywhere that isn't the hero —
 * nav bars, pricing cards, checkout, the assessment gate.
 */
export function OfferCrumb({
  className,
  asLink = true,
  showGlobal = false,
}: {
  className?: string;
  asLink?: boolean;
  showGlobal?: boolean;
}) {
  const visible = useOfferVisible();
  const body = (
    <>
      <Star className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="hidden sm:inline">{OFFER.name}</span>
      <span className="sm:hidden">{OFFER.isFlash ? OFFER.save : "Offer"}</span>
      <span className="text-muted-foreground line-through">{OFFER.was}</span>
      <span className="text-foreground">{OFFER.now}</span>

      {showGlobal && (
        <span className="hidden text-accent md:inline">· {OFFER.globalHighlight}</span>
      )}
    </>
  );

  const classes = cn(
    "inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10",
    "px-3 py-1.5 text-xs font-semibold text-primary",
    className,
  );

  if (!visible) return null;

  if (!asLink) {
    return <span className={classes}>{body}</span>;
  }

  return (
    <Link
      to="/payment"
      aria-label={`${OFFER.name} — ${OFFER.now} with code ${OFFER.code}, MRP ${OFFER.was}`}
      className={cn(
        classes,
        "transition-colors hover:bg-primary/20",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
    >
      {body}
    </Link>
  );
}
